import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import styles from './Video.module.scss';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type VideoProps = {
  fields: {
    title?: Field<string>;
    description?: Field<string>;
    isMediaBlock?: Field<boolean>;
    playlistID?: Field<string>;
    videoID?: Field<string>;
  };
  rendering: {
    uid: string;
    params?: {
      maxVideoCount: string;
      anchorId: string;
    };
  };
  articleBody?: boolean;
};

const getVideoSize = () => {
  const widthHeightRatio = 1.7;
  return 1400 / window.innerHeight > widthHeightRatio
    ? window.innerHeight * widthHeightRatio
    : window.devicePixelRatio >= 1.125
    ? 1400 * 0.85
    : 1400;
};

const Video: React.FC<VideoProps> = ({ fields, rendering, articleBody = false }) => {
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const singleVideo = !!fields.videoID?.value;

  const currentOffsetWidth = useRef<number | null>(null);

  const maxVideoCount = parseInt(rendering?.params?.maxVideoCount || '6');
  const deviceType = useWindowDimensions();

  const packshotHeight = useMemo(() => {
    if (deviceType === 'desktop') {
      if (maxVideoCount % 2 === 0 && maxVideoCount < 6) return '320px';
      if (maxVideoCount % 2 === 1 || maxVideoCount === 6) return '210px';
    }
    if (deviceType === 'tablet') return '190px';
    return '210px';
  }, [deviceType, maxVideoCount]);

  const itemWidth = useMemo(() => {
    if (deviceType === 'desktop') {
      if (maxVideoCount % 2 === 0 && maxVideoCount < 6) return '570px';
      if (maxVideoCount % 2 === 1 || maxVideoCount === 6) return '370px';
    }
    if (deviceType === 'tablet') return '330px';
    return '345px';
  }, [deviceType, maxVideoCount]);

  const resizeVideo = useCallback(() => {
    if (!videoContainerRef.current) return;

    const player = videoContainerRef.current.querySelector(
      '.kv-kulu-container'
    ) as HTMLElement | null;

    if (!player) return;
    if (player.offsetWidth !== currentOffsetWidth.current) {
      if (window.innerWidth >= 768) {
        // Maintain aspect ratio (5.39 for 16:9)
        player.style.height = `${player.offsetWidth / 4.5}px`;
      } else {
        const videos = Array.from(
          videoContainerRef.current.querySelectorAll('.kv-kulu')
        ) as HTMLElement[];
        const currentVideoCount = Math.min(videos.length, maxVideoCount);
        player.style.height = `${player.offsetWidth * 0.59 * currentVideoCount}px`;
        const titles = Array.from(
          videoContainerRef.current.querySelectorAll('.kv-kulu-bottom')
        ) as HTMLElement[];
        for (let i = 0; i < currentVideoCount; i++) {
          videos[i].style.margin = `0 0 ${titles[i].offsetHeight}px 0`;
        }
      }

      currentOffsetWidth.current = player.offsetWidth;
    }
  }, [maxVideoCount]);

  const setWidgetSettings = useCallback(() => {
    if (window.KV?.widget) {
      window.KV.widget({
        guid: fields.playlistID?.value,
        type: 'grid',
        sdkUrl: 'https://unpkg.com/@enghouse-qumu/player-sdk@3.0.3/dist/index.umd.js',
        selector: `#container_${rendering.uid}`,
        showInfoPanel: false,
        hideEmbedButton: true,
        sort: [],
        item: {
          width: itemWidth,
          height: 'auto',
        },
        packshot: {
          width: '100%',
          height: packshotHeight,
          maxWidth: '330px',
          anchor: 'center',
        },
        player: {
          width: getVideoSize(),
          maxWidth: getVideoSize(),
          height: '100%',
          maxHeight: 'auto',
        },
      });
    }
  }, [fields.playlistID?.value, itemWidth, packshotHeight, rendering.uid]);

  const setThumbnailSettings = useCallback(() => {
    if (window.KV?.thumbnail) {
      window.KV.thumbnail({
        selector: `#container_${rendering.uid}`,
        guid: fields.videoID?.value,
        type: 'thumbnail',
        sdkUrl: 'https://unpkg.com/@enghouse-qumu/player-sdk@3.0.3/dist/index.umd.js',
        showInfoPanel: false,
        item: {
          width: '100%',
          height: '100%',
        },
        thumbnail: {
          width: '95%',
          maxWidth: '95%',
        },
        player: {
          width: getVideoSize(),
          maxWidth: getVideoSize(),
          height: 'auto',
        },
      });
    }
  }, [fields.videoID?.value, rendering.uid]);

  useEffect(() => {
    const loadQumuData = () => {
      if (singleVideo) {
        resizeVideo();
        setThumbnailSettings();
      } else {
        resizeVideo();
        setWidgetSettings();
      }
    };

    if (window.KV?.widget) {
      loadQumuData();
    } else {
      const script = document.createElement('script');
      script.onload = loadQumuData;
      script.async = true;
      script.defer = true;
      script.src = 'https://abrdn.qumucloud.com/widgets/application.js';
      document.head.appendChild(script);
    }

    const observer = new ResizeObserver(resizeVideo);
    if (videoContainerRef.current) {
      observer.observe(videoContainerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [singleVideo, resizeVideo, setWidgetSettings, setThumbnailSettings]);

  useEffect(() => {
    if (!videoContainerRef.current) return;
    if (maxVideoCount < 6) {
      const videos = Array.from(
        videoContainerRef.current.getElementsByClassName('kv-kulu')
      ) as HTMLElement[];
      videos.slice(maxVideoCount).forEach((video) => {
        video.style.display = 'none';
      });
    }
  }, [maxVideoCount]);

  const {
    sitecoreContext: { pageEditing },
  } = useSitecoreContext();
  const isInMediaBlock = !!fields.isMediaBlock?.value;
  const renderTitle = !isInMediaBlock && (!!fields?.title?.value || pageEditing);
  const renderDescription = !isInMediaBlock && (!!fields?.description?.value || pageEditing);

  return (
    <section
      id={rendering?.params?.anchorId || rendering?.uid}
      data-e2e-component-uid={rendering?.uid}
      className={classNames(
        'd-flex default-section-padding',
        { [styles.fullWidth]: articleBody },
        { 'background-white': !articleBody }
      )}
    >
      <div className="container">
        {(renderTitle || renderDescription) && (
          <div className="mb-3 mb-md-4 row">
            <div className="col-12 col-lg-8">
              {renderTitle && <Text className="mb-3" field={fields.title} tag="h2" />}
              {renderDescription && (
                <Text className="body-default text-grey" field={fields.description} tag="h2" />
              )}
            </div>
          </div>
        )}
        <div className={classNames({ [styles.videoWrapper]: singleVideo, 'w-100': !singleVideo })}>
          <div
            className={classNames(styles.videoContainer, {
              [styles.singleVideo]: singleVideo,
            })}
          >
            <div id={`container_${rendering.uid}`} ref={videoContainerRef}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Video;
